import { z } from "zod";

// Mirrors the DB `pgEnum` `eval_run_status` (source of truth:
// `@kuzco/db` EvalRunsTable `EVAL_RUN_STATUSES`). Kept inline so this leaf model
// carries no `@kuzco/db` dependency; must stay in sync with that enum.
const evalRunStatusSchema = z.enum([
  "pending",
  "running",
  "completed",
  "failed",
  "cancelled",
]);

/**
 * Per-run result summary shown under an eval group. Mirrors the eval-service
 * `EvalRunSummary` read model: run identity + status + rolled-up sample stats.
 * `provider` is the route's provider name, or null when the route is gone. It
 * is a plain string, not a fixed enum: `normalizeEvalProviderName` passes a
 * custom (BYOK) provider's name through unchanged, so a closed enum here would
 * reject those rows and 500 the whole list.
 */
export const evalRunSummarySchema = z.object({
  id: z.string(),
  evalRunGroupId: z.string(),
  provider: z.string().nullable(),
  modelId: z.string(),
  status: evalRunStatusSchema,
  startedAt: z.date().nullable(),
  completedAt: z.date().nullable(),
  errorMessage: z.string().nullable(),
  isSharedWithSource: z.boolean(),
  judgesOriginalResponses: z.boolean(),
  avgScore: z.number().nullable(),
  completedCount: z.number(),
  failedCount: z.number(),
  totalCount: z.number(),
  p90TargetLatencyMs: z.number().nullable(),
  targetReasoningEfforts: z.array(z.string()),
  noReasoningSampleCount: z.number(),
});

// The group's nested runs drop `evalRunGroupId` (redundant under the group).
export const evalRunSummaryWithoutGroupIdSchema = evalRunSummarySchema.omit({
  evalRunGroupId: true,
});

/**
 * Runtime-detail fields merged onto each run in the group DETAIL view
 * (`eval.getGroup`). Explicitly null for a non-superadmin caller; for a
 * superadmin they come from a per-run lookup that the type treats as possibly
 * absent, so each field is `.nullish()` (nullable AND optional) to match.
 */
const evalRunRuntimeDetailSchema = z.object({
  statusDetail: z.string().nullish(),
  lastHeartbeatAt: z.date().nullish(),
  runtimeProvider: z.string().nullish(),
  runtimeProviderRunId: z.string().nullish(),
  runtimeProviderContainerId: z.string().nullish(),
  runtimeProviderTarget: z.string().nullish(),
  retriedFromRunId: z.string().nullish(),
});

const evalRunWithRuntimeSchema = evalRunSummaryWithoutGroupIdSchema.merge(
  evalRunRuntimeDetailSchema,
);

/**
 * The stored `eval_run_groups` row. `name`/`analysisRunId`/`judgeRouteId`/
 * `createdBy` are nullable columns; the `source*` and `judgeCanonicalModelAlias`
 * columns are non-null with defaults.
 */
export const evalRunGroupRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  rubricId: z.string(),
  rubricVersionId: z.string(),
  evalDatasetId: z.string(),
  analysisRunId: z.string().nullable(),
  judgeRouteId: z.string().nullable(),
  judgeCanonicalModelAlias: z.string(),
  sampleSize: z.number(),
  createdBy: z.string().nullable(),
  teamId: z.string(),
  sourceTeamId: z.string(),
  sourceProjectId: z.string(),
  isSharedWithSource: z.boolean(),
  createdAt: z.date(),
});

/**
 * A single eval group with per-run detail (`eval.getGroup`): the group row plus
 * a display cost, the masked judge route, resolved judge provider/model, and the
 * runs with their runtime-detail fields. `judgeRouteId` overrides the row's raw
 * value with a team-masked one (still `string | null`).
 */
export const evalGroupDetailSchema = evalRunGroupRowSchema.extend({
  evalCostUsd: z.number().nullable(),
  judgeRouteId: z.string().nullable(),
  judgeProvider: z.string(),
  judgeModelId: z.string(),
  runs: z.array(evalRunWithRuntimeSchema),
});

/**
 * One eval group in the per-rubric list (`eval.listGroups`): the group row plus
 * resolved judge provider/model and its runs (no runtime detail, no cost).
 */
export const evalGroupSummarySchema = evalRunGroupRowSchema.extend({
  judgeProvider: z.string(),
  judgeModelId: z.string(),
  runs: z.array(evalRunSummaryWithoutGroupIdSchema),
});

/**
 * One model's score histogram in a comparison (`eval.getScoreDistribution`).
 * `provider` is widened to `z.string()` because BYOK provider names bypass the
 * `Provider` union (matches every other eval `provider` field).
 */
export const evalScoreDistributionSchema = z.object({
  runId: z.string(),
  modelId: z.string(),
  provider: z.string().nullable(),
  distribution: z.array(
    z.object({
      bucketMin: z.number(),
      bucketMax: z.number(),
      count: z.number(),
    }),
  ),
});

/** Score distribution across a comparison's runs, with the rubric's score range. */
export const evalScoreDistributionResponseSchema = z.object({
  scoreMin: z.number(),
  scoreMax: z.number(),
  distributions: z.array(evalScoreDistributionSchema),
});

/**
 * Rubric + dataset context for a comparison (`eval.getGroupContext`).
 * `datasetInferenceCount` is a bigint `mode:"number"` column; `datasetType` is
 * the dataset's plain text type ("eval" | "training").
 */
export const evalGroupContextSchema = z.object({
  rubricId: z.string(),
  rubricName: z.string(),
  evalRunName: z.string().nullable(),
  rubricVersionNumber: z.number(),
  versionId: z.string(),
  scoreMin: z.number(),
  scoreMax: z.number(),
  datasetId: z.string(),
  datasetName: z.string(),
  datasetInferenceCount: z.number(),
  datasetType: z.string(),
});

/**
 * A callable model route enriched with catalog metadata
 * (`eval.listCallableModelsEnriched`). All per-token cost fields are decimal
 * strings (the canonical wire form), null when the model publishes no rate.
 */
export const enrichedCallableModelSchema = z.object({
  routeId: z.string(),
  canonicalAlias: z.string(),
  displayName: z.string(),
  providerName: z.string(),
  providerSlug: z.string().nullable(),
  providerTeamId: z.string(),
  costInputPerToken: z.string().nullable(),
  costOutputPerToken: z.string().nullable(),
  costCachedInputPerToken: z.string().nullable(),
  costReasoningPerToken: z.string().nullable(),
  maxContextSize: z.number(),
  maxOutputTokens: z.number(),
  reasoningSupported: z.boolean(),
  responseStructuredOutputsSupported: z.boolean(),
  toolsSupported: z.boolean(),
  isJudgeModel: z.boolean(),
  isTrainable: z.boolean(),
});

/**
 * Model pricing across callable and pricing-only routes
 * (`eval.listModelPricing`). Per-token costs are decimal strings; `creator` is
 * the lab that built the model.
 */
export const modelAliasPricingSchema = z.object({
  canonicalAlias: z.string(),
  costInputPerToken: z.string().nullable(),
  costOutputPerToken: z.string().nullable(),
  costCachedInputPerToken: z.string().nullable(),
  creator: z.string(),
  releaseDate: z.string().nullable(),
  artificialAnalysisScore: z.number().nullable(),
  artificialAnalysisUrl: z.string().nullable(),
  maxContextSize: z.number(),
  supportsImageInput: z.boolean(),
  supportsToolCalling: z.boolean(),
  supportsReasoning: z.boolean(),
});

/** `evalRun.pollGroupStatus` — the group id plus its runs' live status. */
export const evalPollGroupStatusSchema = z.object({
  groupId: z.string(),
  runs: z.array(evalRunSummaryWithoutGroupIdSchema),
});

/**
 * One shared comparison in `sharedEval.listShared`. `createdAt` is an ISO
 * string (the service `.toISOString()`s the group-level timestamp), while the
 * nested run summaries keep raw `Date` start/complete times.
 */
export const sharedEvalListItemSchema = z.object({
  id: z.string(),
  rubricName: z.string(),
  rubricId: z.string(),
  rubricVersionNumber: z.number(),
  versionId: z.string(),
  sharedByTeamId: z.string(),
  sharedByTeamName: z.string().nullable(),
  createdAt: z.string(),
  runs: z.array(evalRunSummaryWithoutGroupIdSchema),
});

/** `sharedEval.getSharedGroup` — a shared group's identity + its runs. */
export const sharedEvalGroupSchema = z.object({
  id: z.string(),
  rubricId: z.string(),
  rubricVersionId: z.string(),
  evalDatasetId: z.string(),
  sourceTeamId: z.string(),
  isSharedWithSource: z.boolean(),
  createdAt: z.string(),
  runs: z.array(evalRunSummaryWithoutGroupIdSchema),
});

/**
 * `sharedEval.getSharedGroupContext` — rubric + dataset context for a shared
 * comparison, including the full judge `template`.
 */
export const sharedEvalGroupContextSchema = z.object({
  rubricId: z.string(),
  rubricName: z.string(),
  rubricVersionNumber: z.number(),
  versionId: z.string(),
  template: z.string(),
  scoreMin: z.number(),
  scoreMax: z.number(),
  datasetId: z.string(),
  datasetName: z.string(),
  datasetInferenceCount: z.number(),
});

/** `sharedEval.getShareStatus` — super-admin share state for a group. */
export const sharedEvalShareStatusSchema = z.object({
  isShared: z.boolean(),
  sourceTeamId: z.string(),
  sourceTeamName: z.string().nullable(),
  sharedRuns: z.array(
    z.object({
      id: z.string(),
      modelId: z.string(),
      provider: z.string().nullable(),
      isShared: z.boolean(),
    }),
  ),
});
